import React, {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import DefaultPage from '../../../layout/common/DefaultPage';
import VisualizationStep3ActionMenu from '../step3/VisualizationStep3ActionMenu';
import {MRAppRoutes, useMatchedRoute} from '../../../router/routes';
import {
  isScenarioDTO,
  isScenarioRunning,
  useScenarioDetail,
  useScenarioDetailLoaderWithParams,
} from '../../../../store/scenario';
import {Tab, Tabs} from '@mui/material';
import {ExecutionSetupType, ScenarioDetailDTO} from 'hemwb-api';
import {useModel} from '../../../../store/model';
import {useModelInput} from '../../../../store/modelInputs';
import SideBar from '../../../layout/common/SideBar';
import ScenarioParameters from '../../../core/ModelInput/ScenarioParameters/ScenarioParameters';
import VisualizationStepper, {VisualizationSteps} from '../common/VisualizationStepper';
import {useAnalysisLoader} from '../common/useAnalysisLoader';
import {useSelector} from '../../../../store/rootReducer';
import {visualizationSelector} from '../../../../store/visualization';
import {useAuth} from '../../../../store/auth';
import {useUserExpertise} from '../../../../store/userExpertise';

const VisualizationParametersDisplay: React.FC = () => {
  const {modelUuid, scenarioId} = useMatchedRoute(MRAppRoutes.VISUALIZATION_PARAMETERS_DISPLAY);
  const loadScenarioDetail = useScenarioDetailLoaderWithParams();

  const [tab, setTab] = useState(0);
  const [instancesTab, setInstancesTab] = useState(0);
  const [scenarios, setScenarios] = useState<ScenarioDetailDTO[]>([]);
  const [associatedScenarios, setAssociatedScenarios] = useState<ScenarioDetailDTO[]>([]);

  const detail = useScenarioDetail(scenarioId);
  const {auth} = useAuth();
  const user = auth.loggedInUser;
  const userid = user?.id ?? '';

  const [expertiseLevel, setExpertiseLevel] = useState<number>();

  // Auto-switch to Calibration Parameters tab when detail is calibration
  useEffect(() => {
    if (detail?.executionSetup?.type === ExecutionSetupType.CALIBRATION) {
      setTab(2);
    }
  }, [detail?.executionSetup?.type]);

  // Add detail to scenarios when it loads
  useEffect(() => {
    if (detail) {
      setScenarios((prev) => {
        // Only add if not already present
        if (!prev.find((s) => s.id === detail.id)) {
          return [detail, ...prev];
        }
        return prev;
      });
    }
  }, [detail]);

  const selection = useSelector(visualizationSelector).selection;

  useAnalysisLoader(modelUuid, scenarioId);

  // Load additional scenarios from selection
  useEffect(() => {
    if (selection?.scenarios && selection.scenarios.length > 0) {
      Promise.all(selection.scenarios.map((s) => loadScenarioDetail([s.id]) as Promise<ScenarioDetailDTO>)).then(
        (details) => {
          setScenarios((prev) => {
            const newScenarios = details.filter((d) => d && !prev.find((s) => s.id === d.id));
            return [...prev, ...newScenarios];
          });
        },
      );
    }
  }, [selection, loadScenarioDetail]);

  // Guard against undefined modelUuid
  const model = useModel(modelUuid || '');

  const tabsData = useMemo(() => scenarios.filter(isScenarioDTO), [scenarios]);

  // NEW: Load calibration scenarios when on Calibration Parameters tab
  useEffect(() => {
    if (tab === 2) {
      const subModelType = selection?.subModel?.type;

      // If detail is calibration analysis, set it as associated scenario
      if (detail?.executionSetup?.type === ExecutionSetupType.CALIBRATION) {
        setAssociatedScenarios([detail]);
      }
      // If submodel type is CALIBRATION, load scenarios from selection
      else if (subModelType === ExecutionSetupType.CALIBRATION && selection?.scenarios) {
        Promise.all(selection.scenarios.map((s) => loadScenarioDetail([s.id]) as Promise<ScenarioDetailDTO>)).then(
          (details) => {
            setAssociatedScenarios(details.filter((d) => d));
          },
        );
      }
      // If submodel type is SCENARIO, load from linked calibrationScenarios
      else if (subModelType === ExecutionSetupType.SCENARIO && tabsData.length > 1) {
        const currentScenario = tabsData[instancesTab + 1];
        if (currentScenario?.calibrationScenarios && currentScenario.calibrationScenarios.length > 0) {
          Promise.all(
            currentScenario.calibrationScenarios.map(
              (association) => loadScenarioDetail([association.id]) as Promise<ScenarioDetailDTO>,
            ),
          ).then((details) => {
            setAssociatedScenarios(details.filter((d) => d));
          });
        } else {
          setAssociatedScenarios([]);
        }
      } else {
        setAssociatedScenarios([]);
      }
    }
  }, [tab, instancesTab, tabsData, detail, selection, loadScenarioDetail]);

  const input1 = useModelInput(detail?.executionSetupId);
  const input2 = useModelInput(tabsData[1]?.executionSetupId);
  const input3 = useModelInput(associatedScenarios[instancesTab]?.executionSetupId);

  // Reset instancesTab when main tab changes
  useEffect(() => {
    setInstancesTab(0);
  }, [tab]);

  const {scenario, definitions, values} = useMemo(() => {
    let input, scenario;

    if (tab === 0) {
      // Display Parameters
      input = input1;
      scenario = tabsData[0];
    } else if (tab === 1) {
      // Scenario Parameters
      input = input2;
      scenario = tabsData[instancesTab + 1];
    } else {
      // Calibration Parameters
      input = input3;
      scenario = associatedScenarios[instancesTab];
    }

    if (!input || !scenario) {
      return {};
    }

    const parsed = input.value ? JSON.parse(input.value) : {};
    const definitions = parsed.definitions || [];
    const values = JSON.parse(scenario.input.value || '{}');

    return {
      scenario,
      definitions,
      values,
    };
  }, [tab, instancesTab, tabsData, input1, input2, input3, associatedScenarios]);

  const canEnableTab = useCallback(
    (index: number, label: string) => {
      if (index === 0) return false; // Display always enabled

      const isCalibrationAnalysis = detail?.executionSetup?.type === ExecutionSetupType.CALIBRATION;
      const subModelType = selection?.subModel?.type;

      // Scenario Parameters tab (index 1)
      if (index === 1 && label === 'Scenario Parameters') {
        // DISABLE for calibration analysis or if subModel type is CALIBRATION
        if (isCalibrationAnalysis || subModelType === ExecutionSetupType.CALIBRATION) {
          return true;
        }
        // ENABLE if subModel type is SCENARIO
        if (subModelType === ExecutionSetupType.SCENARIO) {
          return false;
        }
        return true; // Disable by default
      }

      // Calibration Parameters tab (index 2)
      if (index === 2 && label === 'Calibration Parameters') {
        // ENABLE for calibration analysis or if subModel type is CALIBRATION
        if (isCalibrationAnalysis || subModelType === ExecutionSetupType.CALIBRATION) {
          return false;
        }
        // NEW: ENABLE if scenario subModel has associated calibration scenarios
        if (subModelType === ExecutionSetupType.SCENARIO) {
          // Check if we have loaded associatedScenarios OR if any scenario has calibrationScenarios
          if (associatedScenarios.length > 0) {
            return false;
          }
          const scenarioWithCalibration = tabsData
            .slice(1)
            .find((s) => s.calibrationScenarios && s.calibrationScenarios.length > 0);
          if (scenarioWithCalibration) {
            return false;
          }
        }
        return true; // Disable by default
      }

      return true; // Disable by default
    },
    [selection, tabsData, detail, associatedScenarios],
  );

  const expertiseLevelObject = useUserExpertise(modelUuid, userid);

  useEffect(() => {
    if (expertiseLevelObject?.expertiseTypeLevel) {
      setExpertiseLevel(Number(expertiseLevelObject.expertiseTypeLevel.split('-')[0]));
    }
  }, [expertiseLevelObject]);

  const inputListPortalRef = useRef<HTMLDivElement>(null);

  const handleTabChange = useCallback((_event: React.SyntheticEvent, newTab: number) => {
    setTab(newTab);
  }, []);

  const handleInstancesTabChange = useCallback((_event: React.SyntheticEvent, newTab: number) => {
    setInstancesTab(newTab);
  }, []);

  // Show nested tabs logic
  const showNestedTabs = useMemo(() => {
    if (tab === 0) return false; // Display - no nested tabs
    if (tab === 1) return tabsData.length > 1; // Scenario Parameters
    if (tab === 2) {
      // Calibration Parameters - show if we have calibration scenarios
      if (detail?.executionSetup?.type === ExecutionSetupType.CALIBRATION) {
        return true; // Show calibration as nested tab
      }
      return associatedScenarios.length > 0;
    }
    return false;
  }, [tab, tabsData.length, associatedScenarios.length, detail]);

  const nestedTabsData = useMemo(() => {
    if (tab === 1) return tabsData.slice(1); // Scenario Parameters
    if (tab === 2) return associatedScenarios; // Calibration Parameters
    return [];
  }, [tab, tabsData, associatedScenarios]);

  return (
    <>
      <SideBar
        position="left"
        className="leftSelection"
        style={{boxShadow: '5px 0px 4px rgba(0,0,0,0.3)', position: 'relative', zIndex: 2}}
      >
        <h3 style={{paddingLeft: 15}}>Parameters</h3>
        <div ref={inputListPortalRef} />
      </SideBar>
      <DefaultPage style={{position: 'relative'}}>
        <VisualizationStepper step={VisualizationSteps.STEP_3} />
        <div style={{marginLeft: '4rem', marginTop: '4rem'}}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="inherit"
            className="tabs-secondary"
          >
            {['Display Parameters', 'Scenario Parameters', 'Calibration Parameters'].map((label, index) => (
              <Tab key={index} value={index} label={label} disabled={canEnableTab(index, label)} />
            ))}
          </Tabs>
          {model && scenario && definitions && values && (
            <div style={{marginTop: '4rem'}}>
              {showNestedTabs && (
                <Tabs
                  value={instancesTab}
                  onChange={handleInstancesTabChange}
                  indicatorColor="primary"
                  textColor="inherit"
                  className="tabs-secondary"
                >
                  {nestedTabsData.map((scenarioData, index) => (
                    <Tab key={scenarioData.id} value={index} label={scenarioData.name} />
                  ))}
                </Tabs>
              )}

              <ScenarioParameters
                expertiseLevel={expertiseLevel ?? 500}
                key={scenario.id}
                subModelType={
                  tab === 0
                    ? ExecutionSetupType.DISPLAY
                    : tab === 2
                    ? ExecutionSetupType.CALIBRATION
                    : selection?.subModel?.type!
                }
                definitions={definitions}
                values={values}
                submitting={false}
                inputListPortalRef={inputListPortalRef}
                renderStatic
                modelUuid={modelUuid}
              />
            </div>
          )}
        </div>
      </DefaultPage>
      <VisualizationStep3ActionMenu
        modelUuid={modelUuid}
        scenarioId={scenarioId}
        model={model}
        subModel={selection?.subModel}
        scenario={scenario}
        scenarioInProgress={isScenarioRunning(scenario?.status)}
      />
    </>
  );
};

export default VisualizationParametersDisplay;
